import type { Certificate, Secret, NetworkPolicy, VulnerabilityFinding, SBOMComponent, Service } from "@coremonit/types";
import { makeRng, int, pick, chance } from "./rng";

export function generateCertificates(services: Service[], seed = 42): Certificate[] {
  const rng = makeRng(seed);
  return services.slice(0, 12).map((s, i): Certificate => {
    const daysLeft = int(rng, -5, 200);
    return {
      id: `cert_${i}`,
      subject: `CN=${s.name}.svc.cluster.local`,
      issuer: "CN=CoreMonit Internal CA",
      expiresAt: new Date(Date.now() + daysLeft * 86400000).toISOString(),
      status: daysLeft < 0 ? "expired" : daysLeft < 30 ? "expiring" : "valid",
      serviceId: s.id,
    };
  });
}

export function generateSecrets(services: Service[], seed = 42): Secret[] {
  const rng = makeRng(seed);
  return services.flatMap((s, i) => [
    { id: `sec_${i}_a`, name: `${s.name}-secret`, kind: "generic" as const, createdAt: new Date(Date.now() - int(rng, 30, 400) * 86400000).toISOString(), rotatedAt: chance(rng, 50) ? new Date(Date.now() - int(rng, 5, 90) * 86400000).toISOString() : undefined },
    { id: `sec_${i}_b`, name: `${s.name}-tls`,    kind: "tls"     as const, createdAt: new Date(Date.now() - int(rng, 30, 400) * 86400000).toISOString(), rotatedAt: new Date(Date.now() - int(rng, 5, 30)  * 86400000).toISOString() },
  ]);
}

export function generateNetworkPolicies(services: Service[], seed = 42): NetworkPolicy[] {
  const rng = makeRng(seed);
  return services.slice(0, 10).map((s, i) => ({
    id: `np_${i}`,
    name: `${s.name}-netpol`,
    namespace: s.deployment.namespace ?? "default",
    ingress: int(rng, 1, 5),
    egress: int(rng, 1, 7),
    enforced: chance(rng, 80),
  }));
}

const CVES = [
  "CVE-2026-11742", "CVE-2026-10921", "CVE-2025-8892", "CVE-2026-14501",
  "CVE-2026-12045", "CVE-2025-9812", "CVE-2026-15012",
];
const COMPONENTS = [
  "openssl", "libxml2", "log4j-core", "spring-core", "express", "prisma", "next",
  "fastify", "aws-sdk", "pg", "mongodb-driver", "kafka-client",
];

export function generateVulnerabilities(services: Service[], seed = 42): VulnerabilityFinding[] {
  const rng = makeRng(seed);
  const out: VulnerabilityFinding[] = [];
  services.forEach((s, i) => {
    const n = int(rng, 0, 4);
    for (let k = 0; k < n; k++) {
      const sev = pick(rng, ["critical", "high", "medium", "low"] as const);
      out.push({
        id: `vuln_${i}_${k}`,
        severity: sev,
        cve: pick(rng, CVES),
        component: pick(rng, COMPONENTS),
        version: `${int(rng, 1, 9)}.${int(rng, 0, 20)}.${int(rng, 0, 40)}`,
        fixedIn: chance(rng, 70) ? `${int(rng, 1, 9)}.${int(rng, 0, 20)}.${int(rng, 0, 40)}` : undefined,
        serviceId: s.id,
      });
    }
  });
  return out;
}

export function generateSBOM(seed = 42): SBOMComponent[] {
  const rng = makeRng(seed);
  return COMPONENTS.map((name, i) => ({
    name,
    version: `${int(rng, 1, 9)}.${int(rng, 0, 20)}.${int(rng, 0, 40)}`,
    license: pick(rng, ["MIT", "Apache-2.0", "BSD-3-Clause", "GPL-3.0"]),
    direct: i < 8,
  }));
}
